import { createContext } from 'react';
import type { ListKey, ListView } from 'types/listViews';
import { myCasesFilter } from '../filter-pieces/filterConstants';

/**
 * The URL param naming a list's active view. Not `view`, which belongs to the group-by and layout
 * switches (`utils/viewParam`).
 */
export const SAVED_VIEW_PARAM_KEY = 'savedView';

/** A view every user has, standing in for one of a list's sub-tabs in the new design. */
export type BuiltInView = {
    /** The `savedView` value. */
    id: string;
    labelId: string;
    tooltipId?: string;
    /** The `qf` / `mf` the view opens its list with when its URL carries none. */
    searchParams?: string;
};

export const CASE_VIEWS: BuiltInView[] = [
    { id: 'all', labelId: 'views.all-cases' },
    { id: 'my', labelId: 'matters.my-cases', searchParams: `qf=${myCasesFilter}` },
];

export const CONTACT_VIEWS: BuiltInView[] = [{ id: 'all', labelId: 'views.all-contacts' }];

/** The one built-in view of a list without sub-tabs, where the list has no name for it of its own. */
export const ALL_VIEW: BuiltInView = { id: 'all', labelId: 'all' };

export const TIMECARD_VIEWS: BuiltInView[] = [
    { id: 'main', labelId: 'timecards.tabs.main' },
    { id: 'draft', labelId: 'timecards.tabs.draft' },
];

/** `search` naming the view, every other param kept. */
export const withSavedView = (search: string, viewId: string): string => {
    const params = new URLSearchParams(search);
    params.set(SAVED_VIEW_PARAM_KEY, viewId);
    return `?${params.toString()}`;
};

/**
 * The active view's {@link BuiltInView.searchParams}, provided by the page showing the view and read by
 * the list that owns the URL as its `initialSearchParams`. A context because the page renders the list
 * through a table component of its own.
 */
export const ViewSearchParamsContext = createContext<string | undefined>(undefined);

/**
 * What a list page, or a list in a record's tab, with built-in views of its own tells its list, which renders the
 * views row. A list without one gets {@link ALL_VIEW}.
 */
export type ListViewsPage = {
    listKey: ListKey;
    views: BuiltInView[];
    /** The built-in view the page shows. */
    activeId: string;
    /** The path showing a built-in view, where the view is part of the path. */
    pathFor?: (viewId: string) => string;
    /** Opens a built-in view; by default the list navigates to it. */
    onSelect?: (viewId: string) => void;
};

export const ListViewsPageContext = createContext<ListViewsPage | undefined>(undefined);

/** The URL params a view keeps: a list's filters and sort, never its search text. */
const VIEW_QUERY_KEYS = ['qf', 'mf', 'sort'] as const;

/** The part of `search` a view keeps, in one canonical order. */
export const viewQueryOf = (search: string): string => {
    const params = new URLSearchParams(search);
    const kept = new URLSearchParams();
    VIEW_QUERY_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) kept.set(key, value);
    });
    return kept.toString();
};

/** A view's `query` split by `keep`: its sort and the filter clauses `keep` holds, then the other clauses. */
export const splitViewQuery = (query: string, keep: (clause: string) => boolean) => {
    const kept = new URLSearchParams(viewQueryOf(query));
    const rest = new URLSearchParams();
    (['qf', 'mf'] as const).forEach((key) => {
        const clauses = kept.get(key)?.split(';') ?? [];
        const others = clauses.filter((clause) => !keep(clause));
        if (!others.length) return;
        const own = clauses.filter(keep);
        if (own.length) kept.set(key, own.join(';'));
        else kept.delete(key);
        rest.set(key, others.join(';'));
    });
    return { kept: viewQueryOf(kept.toString()), rest: rest.toString() };
};

/**
 * Whether two view queries hold the same filters and sort. Clause order is not compared: a list writes its
 * clauses in its fields' order, and a view saved in a record's tab appends the ones the tab leaves out.
 */
export const sameViewQuery = (a: string, b: string): boolean => {
    const sorted = (query: string) => {
        const params = new URLSearchParams(viewQueryOf(query));
        (['qf', 'mf'] as const).forEach((key) => {
            const clauses = params.get(key);
            if (clauses) params.set(key, clauses.split(';').sort().join(';'));
        });
        return params.toString();
    };
    return sorted(a) === sorted(b);
};

/** `query` with `extra`'s filter clauses added after its own. */
export const joinViewQuery = (query: string, extra: string): string => {
    const params = new URLSearchParams(query);
    new URLSearchParams(extra).forEach((clauses, key) =>
        params.set(key, [params.get(key), clauses].filter(Boolean).join(';'))
    );
    return viewQueryOf(params.toString());
};

/** `search` naming the view, with the view's filters and sort in place of its own; every other param kept. */
export const withViewQuery = (search: string, viewId: string | number, query = ''): string => {
    const params = new URLSearchParams(search);
    VIEW_QUERY_KEYS.forEach((key) => params.delete(key));
    params.set(SAVED_VIEW_PARAM_KEY, String(viewId));
    new URLSearchParams(viewQueryOf(query)).forEach((value, key) => params.set(key, value));
    return `?${params.toString()}`;
};

/** A saved view opened on `pathname`, as a link to it carries it. */
export const viewLocation = (pathname: string, view: Pick<ListView, 'id' | 'state'>): string =>
    `${pathname}${withViewQuery('', view.id, view.state.query)}`;
